/**
 * Partner directory (docs/vendor-apps-contract.md §5). A curated static list
 * served from /partners.json and mirrored to the public repo, where
 * getting listed is a reviewed PR. In the product it only PRE-FILLS the
 * Admin → Apps form — a listed app still goes through the clinic's own BAA
 * confirmation and enable dialog. `verified` is our review, not their BAA.
 */

export const PARTNERS_URL = '/partners.json';
export const CATEGORIES = ['hep', 'rtm', 'outcomes', 'force-plate', 'scheduling', 'billing', 'other'];

const httpsUrl = (v) => {
  try {
    const u = new URL(String(v));
    return u.protocol === 'https:' ? u : null;
  } catch (e) {
    return null;
  }
};

/** Drop anything malformed rather than let a bad directory entry prefill a bad registration. */
export function normalizePartner(p) {
  if (!p || typeof p !== 'object') return null;
  const launch = httpsUrl(p.launchUrl);
  if (!launch) return null;
  const embedOrigin = String(p.embedOrigin || launch.origin).replace(/\/+$/, '');
  if (embedOrigin.toLowerCase() !== launch.origin.toLowerCase()) return null;
  const name = String(p.name || '').trim();
  if (!name) return null;
  return {
    id: String(p.id || embedOrigin).trim(),
    name,
    category: CATEGORIES.includes(p.category) ? p.category : 'other',
    blurb: String(p.blurb || '').trim().slice(0, 140),
    embedOrigin,
    launchUrl: launch.toString(),
    website: httpsUrl(p.website)?.toString() || null,
    docs: httpsUrl(p.docs)?.toString() || null,
    logo: httpsUrl(p.logo)?.toString() || null,
    verified: p.verified === true,
    listedAt: typeof p.listedAt === 'string' ? p.listedAt : null,
  };
}

export function normalizeDirectory(json) {
  const list = Array.isArray(json?.partners) ? json.partners : [];
  return list.map(normalizePartner).filter(Boolean);
}

/**
 * `origin` is where the web app's files live. Pass it from the Chrome extension,
 * whose own origin (chrome-extension://…) has no partners.json; in the browser the
 * default relative URL is right.
 */
export async function fetchPartners(fetchImpl = typeof fetch === 'function' ? fetch : null, origin = '') {
  if (!fetchImpl) return [];
  try {
    const rsp = await fetchImpl(`${origin || ''}${PARTNERS_URL}`, { cache: 'no-cache' });
    if (!rsp.ok) return [];
    return normalizeDirectory(await rsp.json());
  } catch (e) {
    return [];
  }
}
